import React from "react";
import { ProductBlueprint } from "../../../game/bench-work/blueprint";
import {
  headingVector,
  slotFaceOutline,
} from "../../../game/bench-work/plan-geometry";

/** Blueprint line work: white on blueprint blue. */
export const BLUEPRINT_LINE = "#e8eef5";

/** The real part layout, straight off the product blueprint: the same
 * slots the bench view's ghost outlines stand at, fasteners marked the
 * way a drawing calls out its nails — a circled cross for one driven
 * down, an arrow for one driven in from the side. Drawn by the plan
 * sheet, the plan browser's thumbnails, and the blueprint editor's live
 * schematic. */
export const BlueprintSchematic: React.FC<{
  blueprint: ProductBlueprint;
  className?: string;
}> = ({ blueprint, className = "mx-auto max-h-36 w-full" }) => {
  const pad = 3;
  return (
    <svg
      viewBox={`${-pad} ${-pad} ${blueprint.widthIn + pad * 2} ${blueprint.heightIn + pad * 2}`}
      className={className}
      role="img"
    >
      {[...blueprint.slots]
        .sort((a, b) => a.zIn - b.zIn)
        .map((slot) => {
          // An on-edge part draws as the thin strip it presents from
          // above, a mitered rail with its ends cut — the drawing shows
          // the build the way the bench does
          return (
            <polygon
              key={slot.id}
              points={slotFaceOutline(slot)
                .map((p) => `${p.x},${p.y}`)
                .join(" ")}
              transform={`translate(${slot.xIn} ${slot.yIn}) rotate(${slot.angleDeg})`}
              fill={slot.zIn === 0 ? "rgba(232,238,245,0.08)" : "none"}
              stroke={BLUEPRINT_LINE}
              strokeWidth={0.5}
            />
          );
        })}
      {blueprint.fasteners.map((fastener, index) => {
        if (fastener.level) {
          const { x, y } = headingVector(fastener.level.headingDeg);
          const tipX = fastener.xIn + x * 2.4;
          const tipY = fastener.yIn + y * 2.4;
          return (
            <g key={index} stroke={BLUEPRINT_LINE} strokeWidth={0.4}>
              <line x1={fastener.xIn} y1={fastener.yIn} x2={tipX} y2={tipY} />
              <line
                x1={tipX}
                y1={tipY}
                x2={tipX - x * 0.9 - y * 0.6}
                y2={tipY - y * 0.9 + x * 0.6}
              />
              <line
                x1={tipX}
                y1={tipY}
                x2={tipX - x * 0.9 + y * 0.6}
                y2={tipY - y * 0.9 - x * 0.6}
              />
            </g>
          );
        }
        return (
          <g key={index} stroke={BLUEPRINT_LINE} strokeWidth={0.4}>
            <circle cx={fastener.xIn} cy={fastener.yIn} r={1.4} fill="none" />
            <line
              x1={fastener.xIn - 1}
              y1={fastener.yIn - 1}
              x2={fastener.xIn + 1}
              y2={fastener.yIn + 1}
            />
            <line
              x1={fastener.xIn - 1}
              y1={fastener.yIn + 1}
              x2={fastener.xIn + 1}
              y2={fastener.yIn - 1}
            />
          </g>
        );
      })}
    </svg>
  );
};
